import React from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { motion } from 'framer-motion';

import BlogContentParser from '../../components/common/BlogContentParser';
import SEO from '../../components/SEO';
import useBlogData from '../../hooks/useBlogData';
import THEME from '../../constants/theme';

/**
 * BlogPostPage - Individual Blog Post Detail Page
 * Features:
 * - Full-width cover image with responsive height
 * - Back button navigation
 * - Title, subtitle, and metadata (category, date, read time)
 * - Multiple content sections with visual dividers
 * - Dynamic text formatting via BlogContentParser
 * @returns {React.Component} BlogPostPage component
 */
const BlogPostPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { blog, loading } = useBlogData(id);

  // Show loading spinner while fetching
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress sx={{ color: THEME.COLORS.PRIMARY_BLUE }} />
      </Box>
    );
  }

  // Handle blog not found
  if (!blog) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${THEME.COLORS.PRIMARY_BLUE}08 0%, ${THEME.COLORS.PRIMARY_BLUE}04 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${THEME.COLORS.PRIMARY_BLUE}08 0%, transparent 70%)`,
            top: '-100px',
            right: '-100px',
            pointerEvents: 'none'
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${THEME.COLORS.PRIMARY_BLUE}04 0%, transparent 70%)`,
            bottom: '-50px',
            left: '-50px',
            pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth='sm' sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            {/* Icon */}
            <Box
              sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'center',
                animation: 'float 3s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-10px)' }
                }
              }}
            >
              <SearchOffIcon
                sx={{
                  fontSize: '5rem',
                  color: THEME.COLORS.PRIMARY_BLUE,
                  opacity: 0.8
                }}
              />
            </Box>

            {/* Title */}
            <Typography
              variant='h1'
              sx={{
                mb: 1,
                fontSize: { xs: '1.8rem', sm: '2.5rem' },
                fontWeight: 800,
                color: '#1a1a1a'
              }}
            >
              Blog Post Not Found
            </Typography>

            {/* Error Code */}
            <Box sx={{ mt: 3, mb: 3 }}>
              <Typography
                sx={{
                  fontSize: { xs: '3rem', sm: '4rem' },
                  fontWeight: 700,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  lineHeight: 1,
                  mb: 0.5
                }}
              >
                404
              </Typography>
              <Typography
                sx={{
                  fontSize: '1rem',
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}
              >
                Page Not Available
              </Typography>
            </Box>

            {/* Description */}
            <Typography
              sx={{
                mb: 3,
                color: '#555',
                fontSize: '1rem',
                lineHeight: 1.6,
                maxWidth: '90%',
                mx: 'auto'
              }}
            >
              The blog post you're looking for couldn't be found. This could
              mean the URL is incorrect or the article has been removed.
            </Typography>

            {/* Suggestion */}
            <Box
              sx={{
                mb: 4,
                p: 2,
                borderRadius: '8px',
                background: `${THEME.COLORS.PRIMARY_BLUE}08`,
                border: `1px solid ${THEME.COLORS.PRIMARY_BLUE}20`
              }}
            >
              <Typography sx={{ fontSize: '0.9rem', color: '#666' }}>
                Need help? Browse our <strong>latest posts</strong> or explore
                articles by category to find what you're looking for.
              </Typography>
            </Box>

            {/* Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}
            >
              <Button
                variant='contained'
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/blog')}
                sx={{
                  backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                  color: 'white',
                  px: 3,
                  py: 1.2,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  borderRadius: '6px',
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                    transform: 'translateX(-4px)',
                    boxShadow: `0 8px 20px ${THEME.COLORS.PRIMARY_BLUE}40`
                  }
                }}
              >
                Back to Blog
              </Button>
              <Button
                variant='outlined'
                onClick={() => navigate('/')}
                sx={{
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  px: 3,
                  py: 1.2,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  borderRadius: '6px',
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: THEME.COLORS.PRIMARY_BLUE,
                    backgroundColor: `${THEME.COLORS.PRIMARY_BLUE}10`,
                    transform: 'translateX(4px)'
                  }
                }}
              >
                Go to Home
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  // Format date
  const formattedDate = new Date(blog.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <>
      <SEO
        title={blog.title}
        content={blog.excerpt}
        keywords={`${blog.categories.join(', ')}, ${blog.title}, MarQ Capital blog, investment analysis, market insights, trading strategies, financial commentary`}
        canonical={`/library/${blog.id}`}
        ogImage={blog.coverImage}
        ogType='article'
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: blog.title,
          description: blog.excerpt,
          image: blog.coverImage,
          datePublished: blog.date,
          dateModified: blog.date,
          author: {
            '@type': 'Organization',
            name: 'MarQ Capital Limited',
            url: 'https://mqcapital.co.uk'
          },
          publisher: {
            '@type': 'Organization',
            name: 'MarQ Capital Limited',
            logo: {
              '@type': 'ImageObject',
              url: 'https://mqcapital.co.uk/logo512.png'
            }
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://mqcapital.co.uk/library/${blog.id}`
          },
          keywords: blog.categories.join(', '),
          articleSection: 'Financial Analysis',
          inLanguage: 'en-GB',
          ...(blog.readTime && {
            timeRequired: `PT${blog.readTime}M`
          })
        }}
      />

      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          backgroundColor: THEME.COLORS.NEUTRAL_WHITE
        }}
      >
        {/* Background gradient */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            width: '100%',
            height: '500px',
            background:
              'radial-gradient(ellipse 80% 100% at 50% 0%, rgba(30, 82, 243, 0.08) 0%, rgba(30, 82, 243, 0) 70%)',
            pointerEvents: 'none',
            zIndex: 0
          }}
        />

        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: { xs: 12, sm: 16 },
              zIndex: 10,
              pl: { xs: 2, sm: 4 }
            }}
          >
            <IconButton
              onClick={() => navigate('/blog')}
              sx={{
                backgroundColor: 'rgba(30, 82, 243, 0.1)',
                color: THEME.COLORS.PRIMARY_BLUE,
                border: `1px solid rgba(30, 82, 243, 0.3)`,
                borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                backdropFilter: 'blur(10px)',
                transition: `all ${THEME.TRANSITIONS.MEDIUM}`,
                '&:hover': {
                  backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: THEME.COLORS.NEUTRAL_WHITE,
                  transform: 'translateX(-4px)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Box>
        </motion.div>

        <Container
          maxWidth='md'
          sx={{ position: 'relative', zIndex: 2, py: { xs: 2, sm: 4 } }}
        >
          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Box sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
              {/* Category Chips */}
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {blog.categories.map((cat, index) => (
                  <Chip
                    key={index}
                    label={cat}
                    sx={{
                      backgroundColor:
                        cat === 'PDF'
                          ? 'rgba(220, 53, 69, 0.1)'
                          : 'rgba(30, 82, 243, 0.1)',
                      color:
                        cat === 'PDF' ? '#DC3545' : THEME.COLORS.PRIMARY_BLUE,
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      height: 'auto',
                      padding: '6px 12px',
                      border: `1px solid ${cat === 'PDF' ? 'rgba(220, 53, 69, 0.3)' : 'rgba(30, 82, 243, 0.3)'}`
                    }}
                  />
                ))}
              </Box>

              {/* Title */}
              <Typography
                variant='h1'
                sx={{
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                  fontWeight: 800,
                  lineHeight: 1.2,
                  mb: 2,
                  color: THEME.COLORS.NEUTRAL_BLACK,
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
                }}
              >
                {blog.title}
              </Typography>

              {/* Subtitle */}
              <Typography
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  color: THEME.COLORS.NEUTRAL_BLACK_LIGHT,
                  mb: 3,
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                  lineHeight: 1.6
                }}
              >
                {blog.subtitle}
              </Typography>

              {/* Metadata */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 3 }}
                sx={{
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  color: THEME.COLORS.NEUTRAL_BLACK_LIGHT,
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                  alignItems: { xs: 'flex-start', sm: 'center' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <CalendarTodayIcon
                    sx={{
                      fontSize: '1.1rem',
                      color: THEME.COLORS.PRIMARY_BLUE,
                      flexShrink: 0
                    }}
                  />
                  <span>{formattedDate}</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <AccessTimeIcon
                    sx={{
                      fontSize: '1.1rem',
                      color: THEME.COLORS.PRIMARY_BLUE,
                      flexShrink: 0
                    }}
                  />
                  <span>{blog.readTime} min read</span>
                </Box>
              </Stack>
            </Box>
          </motion.div>

          {/* Divider */}
          <Divider
            sx={{
              my: { xs: 3, sm: 4, md: 5 },
              borderColor: 'rgba(30, 82, 243, 0.15)'
            }}
          />

          {/* Article Content Sections */}
          {blog.sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
            >
              <Box sx={{ mb: { xs: 2.5, sm: 3, md: 4 } }}>
                {/* Section Title */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 2.5,
                    minHeight: '2.5rem'
                  }}
                >
                  <Box
                    sx={{
                      width: '4px',
                      minHeight: '2.5rem',
                      backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                      borderRadius: THEME.BORDERS.RADIUS.SMALL,
                      flexShrink: 0,
                      mt: 0
                    }}
                  />
                  <Typography
                    variant='h2'
                    sx={{
                      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                      fontWeight: 700,
                      color: THEME.COLORS.NEUTRAL_BLACK,
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      lineHeight: 1.3
                    }}
                  >
                    {section.title}
                  </Typography>
                </Box>

                {/* Section Content */}
                <Box
                  sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' },
                    lineHeight: 1.75,
                    color: THEME.COLORS.NEUTRAL_BLACK_LIGHT,
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                    '& strong': {
                      fontWeight: 700,
                      color: THEME.COLORS.NEUTRAL_BLACK
                    }
                  }}
                >
                  <BlogContentParser content={section.content} />
                </Box>
              </Box>

              {/* Section Divider (not on last section) */}
              {index < blog.sections.length - 1 && (
                <Divider
                  sx={{
                    my: { xs: 2.5, sm: 3, md: 3.5 },
                    borderColor: 'rgba(30, 82, 243, 0.25)',
                    opacity: 1
                  }}
                />
              )}
            </motion.div>
          ))}

          {/* Bottom Divider */}
          <Divider
            sx={{
              my: { xs: 4, sm: 5, md: 6 },
              borderColor: 'rgba(30, 82, 243, 0.15)'
            }}
          />

          {/* PDF Download Button */}
          {blog.pdfLink && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: { xs: 3, sm: 4 }
                }}
              >
                <Button
                  href={blog.pdfLink}
                  target='_blank'
                  rel='noopener noreferrer'
                  variant='contained'
                  startIcon={<FileDownloadIcon />}
                  sx={{
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                    color: THEME.COLORS.NEUTRAL_WHITE,
                    padding: { xs: '10px 28px', sm: '12px 36px' },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    fontWeight: 600,
                    borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                    transition: `all ${THEME.TRANSITIONS.MEDIUM}`,
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                    '&:hover': {
                      backgroundColor: THEME.COLORS.PRIMARY_BLUE_DARK,
                      color: THEME.COLORS.NEUTRAL_WHITE,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 16px rgba(30, 82, 243, 0.3)`
                    }
                  }}
                >
                  Download PDF
                </Button>
              </Box>
            </motion.div>
          )}

          {/* Back to Blog Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: { xs: 4, sm: 6 }
              }}
            >
              <Button
                onClick={() => navigate('/blog')}
                variant='outlined'
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: THEME.COLORS.PRIMARY_BLUE,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  backgroundColor: 'rgba(30, 82, 243, 0.05)',
                  padding: { xs: '10px 28px', sm: '12px 36px' },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 600,
                  borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                  transition: `all ${THEME.TRANSITIONS.MEDIUM}`,
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                  '&:hover': {
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                    color: THEME.COLORS.NEUTRAL_WHITE,
                    borderColor: THEME.COLORS.PRIMARY_BLUE,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 16px rgba(30, 82, 243, 0.2)`
                  }
                }}
              >
                Back to All Articles
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default BlogPostPage;
